import { useState, useEffect } from "react"
import { Outlet, useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useAuthStore } from "@/features/auth/store/auth.store"
import { AUTH_ROUTES } from "@/shared/constants/routes.constants"
import {
  LayoutDashboard, User, CreditCard, Globe, FileText,
  HelpCircle, Info, LogOut, Settings, SlidersHorizontal, Contact,
} from "lucide-react"
import "./settings.css"
import SubHeader from "@/clientWeb/components/layout/SubHeader"

interface NavItem {
  id: string
  label: string
  icon: React.ReactNode
  path: string
}

const getNavItems = (t: (key: string) => string): NavItem[] => [
  { id: "notifications",       label: t("settings.notificationsAlerts"),                  icon: <SlidersHorizontal className="w-[18px] h-[18px]" />, path: "/settings/general" },
  { id: "profile",       label: t("settings.profile"),      icon: <User            className="w-[18px] h-[18px]" />, path: "/settings" },
  { id: "licenses",      label: t("settings.licenses"),     icon: <LayoutDashboard className="w-[18px] h-[18px]" />, path: "/settings/licenses" },
  { id: "payment",       label: t("settings.paymentHistory"), icon: <CreditCard      className="w-[18px] h-[18px]" />, path: "/settings/payment-history" },
  { id: "languages",     label: t("settings.languages"),    icon: <Globe           className="w-[18px] h-[18px]" />, path: "/settings/languages" },
  { id: "transcription", label: t("settings.transcription"), icon: <FileText        className="w-[18px] h-[18px]" />, path: "/settings/transcription" },
  // { id: "telepresence",  label: t("settings.telepresence"), icon: <MonitorSpeaker  className="w-[18px] h-[18px]" />, path: "/settings/telepresence" },
  { id: "address-book",  label: t("settings.addressBook"), icon: <Contact         className="w-[18px] h-[18px]" />, path: "/settings/address-book" },
  // { id: "my-files",      label: "My Files",                 icon: <FolderOpen      className="w-[18px] h-[18px]" />, path: "/settings/my-files" },
  { id: "help",          label: t("settings.help"),         icon: <HelpCircle      className="w-[18px] h-[18px]" />, path: "/settings/help" },
  { id: "about",         label: t("settings.about"),        icon: <Info            className="w-[18px] h-[18px]" />, path: "/settings/about" },
]

function resolveActive(pathname: string, navItems: NavItem[]): string {
  const matched = navItems
    .filter(item => pathname === item.path || pathname.startsWith(item.path + "/"))
    .sort((a, b) => b.path.length - a.path.length)
  return matched[0]?.id || "profile"
}

export default function SettingsLayout() {
  const t = useTranslation()
  const navigate  = useNavigate()
  const location  = useLocation()
  const navItems = getNavItems(t)
  const [activeNav, setActiveNav]           = useState(() => resolveActive(location.pathname, navItems))
  const [showLogoutModal, setShowLogoutModal] = useState(false)

  useEffect(() => {
    setActiveNav(resolveActive(location.pathname, navItems))
  }, [location.pathname, navItems])

  const logout = useAuthStore((s) => s.logout)

  const handleNavClick = (item: NavItem) => { navigate(item.path) }
  const handleLogout = async () => {
    setShowLogoutModal(false)
    await logout({ endSecureIdSession: true })
    navigate(AUTH_ROUTES.LOGIN, { replace: true })
  }

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="flex items-center gap-3 px-4 py-4 border-b border-stroke-100 dark:border-border shrink-0">
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Settings className="w-4 h-4 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-md font-bold text-slate-900 dark:text-foreground/80 dark:text-white leading-none">{t("settings.title")}</h1>
          <p className="text-[10px] text-slate-400 dark:text-foreground mt-0.5 truncate">{t("settings.subtitle")}</p>
        </div>
      </div>

      {/* Nav items */}
      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
        {navItems.map((item: NavItem) => {
          const active = activeNav === item.id
          return (
            <button
              key={item.id}
              onClick={() => handleNavClick(item)}
              className={`
                w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
                transition-all duration-150 group
                ${active
                  ? "bg-primary/10 dark:bg-primary/20 text-primar dark:text-foreground"
                  : "text-slate-600 dark:text-muted-foreground hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:text-foreground/80 dark:hover:text-slate-100"
                }
              `}
            >
              <span className={active ? "text-primary dark:text-foreground shrink-0" : "text-slate-400 dark:text-foreground group-hover:text-slate-600 dark:group-hover:text-slate-300 shrink-0 transition-colors"}>
                {item.icon}
              </span>
              <span className="truncate">{item.label}</span>
              {active && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary shrink-0" />}
            </button>
          )
        })}
      </nav>

      {/* Logout */}
      <div className="px-3 py-3 border-t border-border shrink-0">
        <button
          onClick={() => setShowLogoutModal(true)}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-150"
        >
          <LogOut className="w-[18px] h-[18px] shrink-0" />
          <span>{t("auth.logout")}</span>
        </button>
      </div>
    </div>
  )

  return (
    <div className="flex flex-col w-full h-full">
      <SubHeader
          moduleName={t("settings.title")}
          description={''}
          OptionalAction={false}
        />
    <div className="flex-1 p-1 mt-2 sm:mt-0 sm:p-3 min-h-0">
     
      <div className="flex flex-col md:flex-row h-full w-full bg-card rounded-xl shadow-xl overflow-hidden">
 
        {/* Desktop Sidebar */}
        <aside className="hidden rounded-l-xl md:flex w-56 lg:w-60 shrink-0 flex-col border-r border-stroke-100 dark:border-border overflow-hidden">
          <SidebarContent />
        </aside>

        {/* Mobile Header with Title */}
        <div className="md:hidden flex items-center gap-3 px-4 py-3 bg-card border-b border-border shrink-0">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Settings className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-md font-bold text-slate-900 dark:text-foreground/80 dark:text-white leading-none">{t("settings.title")}</h1>
          </div>
        </div>

        {/* Mobile Horizontal Tabs */}
        <div className="md:hidden border-b border-border bg-card">
          <div className="settings-tabs-container flex overflow-x-auto px-4 py-2 gap-1">
            {navItems.map((item: NavItem) => {
              const active = activeNav === item.id
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item)}
                  className={`
                    flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap shrink-0
                    transition-all duration-150 min-w-fit
                    ${active
                      ? "bg-primary/10 dark:bg-primary/20 text-primary dark:text-foreground"
                      : "text-slate-600 dark:text-muted-foreground hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:text-foreground/80 dark:hover:text-slate-100"
                    }
                  `}
                >
                  <span className={active ? "text-primary shrink-0" : "text-slate-400 dark:text-foreground shrink-0 transition-colors"}>
                    {item.icon}
                  </span>
                  <span>{item.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
          <main className="flex-1 overflow-y-auto md:rounded-r-xl">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      {/* Logout Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowLogoutModal(false)} />
          <div className="relative bg-card rounded-2xl shadow-2xl border border-border p-6 w-full max-w-sm">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
              <LogOut className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-foreground/80 dark:text-white text-center mb-1">{t("settings.signOutConfirm")}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-6">{t("settings.signOutDesc")}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-stroke-100 dark:border-border dark:border-border text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
              >
                {t("auth.signOut")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  )
}
